import { bnPageHead } from "@/lib/bn-head";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { JsonLd } from "@/components/JsonLd";
import { BN_POSTS } from "@/content/bn/posts";
import { BN_CATEGORY_SLUGS } from "@/content/bn/categories";
import { SITE } from "@/content/site";
import { ArrowLeft, ArrowUpRight } from "lucide-react";

const TITLE = "বাংলা ব্লগ — ডিজিটাল মার্কেটিং, SEO ও ফ্রিল্যান্সিং";
const DESC =
  "ডিজিটাল মার্কেটিং, SEO, ফেসবুক অ্যাডস, কন্টেন্ট মার্কেটিং এবং ফ্রিল্যান্সিং নিয়ে বাংলায় গভীর গাইড — বাংলাদেশের প্রেক্ষাপটে।";

export const Route = createFileRoute("/bn/blog/")({
  head: () =>
    bnPageHead({
      path: "/bn/blog",
      enPath: "/blog",
      title: TITLE,
      desc: DESC,
      keywords: ["seo bangla tutorial", "বাংলা এসইও ব্লগ", "digital marketing bangla blog"],
      suffix: false,
      twitterCard: false,
    }),
  component: BnBlogIndex,
});

function BnBlogIndex() {
  const posts = [...BN_POSTS].sort((a, b) => (a.date < b.date ? 1 : -1));
  const categories = Object.entries(BN_CATEGORY_SLUGS)
    .map(([slug, name]) => ({
      slug,
      name,
      count: BN_POSTS.filter((p) => p.category === name).length,
    }))
    .filter((c) => c.count > 0)
    .sort((a, b) => b.count - a.count);

  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    inLanguage: "bn-BD",
    itemListElement: posts.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE.url}/bn/blog/${p.slug}`,
      name: p.title,
    })),
  };

  return (
    <>
      <JsonLd data={itemList} />
      <Section className="pt-8 pb-16">
        <Link to="/bn" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> বাংলা হোম
        </Link>
        <h1 className="mt-6 font-display text-4xl sm:text-5xl leading-tight">
          বাংলা <span className="text-gradient">আর্টিকেল</span>
        </h1>
        <p className="mt-4 max-w-2xl text-lg text-muted-foreground">{DESC}</p>

        {categories.length > 0 && (
          <nav aria-label="ক্যাটাগরি" className="mt-8 flex flex-wrap gap-2">
            {categories.map((c) => (
              <Link
                key={c.slug}
                to="/bn/blog/category/$category"
                params={{ category: c.slug }}
                className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-xs text-muted-foreground hover:text-foreground hover:border-primary/40 transition"
              >
                {c.name}
                <span className="text-[10px] tabular-nums opacity-60">{c.count}</span>
              </Link>
            ))}
          </nav>
        )}
      </Section>

      <Section className="pb-24">
        <div className="grid gap-6 sm:grid-cols-2">
          {posts.map((p) => (
            <Link
              key={p.slug}
              to="/bn/blog/$slug"
              params={{ slug: p.slug }}
              className="cv-card group rounded-2xl border border-border bg-card p-6 transition hover:border-primary/40"
            >
              <div className="text-xs uppercase tracking-wider text-muted-foreground">
                {p.category} · {p.readMin} মিনিট পড়া
              </div>
              <h2 className="mt-3 font-display text-xl leading-snug group-hover:text-primary transition">
                {p.title}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{p.excerpt}</p>
              <div className="mt-4 inline-flex items-center gap-1 text-sm text-primary">
                পড়ুন <ArrowUpRight className="h-4 w-4" />
              </div>
            </Link>
          ))}
        </div>
      </Section>
    </>
  );
}
